'use client';

import Image from 'next/image';
import Link from 'next/link';
import { Shirt, DollarSign, Zap, Lock } from 'lucide-react';

const HERO_IMAGE = '/styleai/hero-new.jpeg';

const features = [
  { icon: Shirt, title: 'Real Products', text: 'From top stores' },
  { icon: DollarSign, title: 'All Budgets', text: 'Fits for every price' },
  { icon: Zap, title: 'AI Powered', text: 'Smart outfit picks' },
  { icon: Lock, title: 'Save & Unlock', text: 'More fits. More fire.' },
];

export default function HeroSection() {
  return (
    <section className="relative overflow-hidden bg-black text-white">
      <style jsx>{`
        @keyframes badgeGlow {
          0%,
          100% {
            opacity: 0.92;
            box-shadow:
              0 0 10px rgba(245, 200, 66, 0.25),
              inset 0 0 8px rgba(245, 200, 66, 0.06);
            filter: brightness(1);
          }

          8% {
            opacity: 1;
            box-shadow:
              0 0 22px rgba(245, 200, 66, 0.72),
              0 0 44px rgba(245, 200, 66, 0.28),
              inset 0 0 16px rgba(245, 200, 66, 0.12);
            filter: brightness(1.15);
          }

          14% {
            opacity: 0.82;
            box-shadow:
              0 0 8px rgba(245, 200, 66, 0.18),
              inset 0 0 6px rgba(245, 200, 66, 0.05);
            filter: brightness(0.92);
          }

          22% {
            opacity: 1;
            box-shadow:
              0 0 28px rgba(245, 200, 66, 0.82),
              0 0 56px rgba(245, 200, 66, 0.34),
              inset 0 0 18px rgba(245, 200, 66, 0.15);
            filter: brightness(1.2);
          }

          45%,
          82% {
            opacity: 0.96;
            box-shadow:
              0 0 14px rgba(245, 200, 66, 0.34),
              inset 0 0 10px rgba(245, 200, 66, 0.08);
            filter: brightness(1.02);
          }
        }

        @keyframes dotGlow {
          0%,
          100% {
            opacity: 0.9;
            transform: scale(1);
            box-shadow:
              0 0 10px rgba(245, 200, 66, 0.5),
              0 0 18px rgba(245, 200, 66, 0.18);
          }

          8%,
          22% {
            opacity: 1;
            transform: scale(1.14);
            box-shadow:
              0 0 18px rgba(245, 200, 66, 0.95),
              0 0 36px rgba(245, 200, 66, 0.36);
          }
        }

        .hero-fit-badge {
          animation: badgeGlow 4.2s ease-in-out infinite;
        }

        .hero-fit-dot {
          animation: dotGlow 4.2s ease-in-out infinite;
        }

        @media (prefers-reduced-motion: reduce) {
          .hero-fit-badge,
          .hero-fit-dot {
            animation: none;
          }
        }
      `}</style>

      <div className="relative min-h-[760px] overflow-hidden md:min-h-[720px] lg:min-h-[690px]">
        <Image
          src={HERO_IMAGE}
          alt="FHITZ hero"
          fill
          priority
          className="object-cover object-center"
        />

        <div className="absolute inset-0 hidden bg-[linear-gradient(90deg,rgba(0,0,0,0.58)_0%,rgba(0,0,0,0.34)_38%,rgba(0,0,0,0.08)_100%)] md:block" />
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.78)_0%,rgba(0,0,0,0.35)_45%,rgba(0,0,0,0.92)_100%)] md:hidden" />

        <div className="relative z-10 mx-auto flex min-h-[760px] max-w-[1500px] flex-col justify-end px-5 pb-7 pt-24 md:min-h-[720px] md:px-7 lg:min-h-[690px]">
          <div className="w-full max-w-[680px] text-center md:ml-[3%] md:text-center lg:ml-[6%]">
            <h1 className="text-[46px] font-black uppercase italic leading-[0.9] tracking-[-0.04em] text-white sm:text-[58px] md:text-[72px] lg:text-[94px]">
              Real Fits.
              <br />
              <span className="whitespace-nowrap text-[#f5c842]">
                Your Budget.
              </span>
              <br />
              Your Vibe.
            </h1>

            <p className="mx-auto mt-6 max-w-[520px] rounded-2xl bg-black/35 px-4 py-3 text-[16px] font-medium leading-7 text-white/90 backdrop-blur-sm sm:text-[18px] md:bg-transparent md:p-0 md:text-[19px] md:leading-8">
              Describe the look you want. Set your budget. FHITZ builds a
              complete outfit — piece by piece — in seconds.
            </p>

            <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link
                href="/admin-access"
                className="w-full rounded-xl bg-[#f5c842] px-6 py-3.5 text-center text-sm font-black uppercase tracking-[0.12em] text-black shadow-[0_0_24px_rgba(245,200,66,0.35)] transition-all duration-300 hover:bg-[#ffd65a] hover:shadow-[0_0_34px_rgba(245,200,66,0.55)] sm:w-auto"
              >
                Generate Your Fit Now
              </Link>

              <Link
                href="/early-access"
                className="w-full rounded-xl border border-white/15 bg-black/45 px-6 py-3.5 text-center text-sm font-bold uppercase tracking-[0.12em] text-white transition-all duration-300 hover:border-[#f5c842]/70 hover:text-[#f5c842] sm:w-auto"
              >
                Get Unlimited Access
              </Link>
            </div>
          </div>

          <div className="mt-8 max-w-[820px] rounded-2xl border border-white/10 bg-black/55 p-4 backdrop-blur-md md:mt-10 md:bg-black/35 lg:max-w-[760px] lg:border-0 lg:bg-transparent lg:p-0 lg:backdrop-blur-0">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {features.map((feature) => {
                const Icon = feature.icon;

                return (
                  <div
                    key={feature.title}
                    className="flex items-center gap-3 rounded-xl bg-black/30 p-3 lg:bg-transparent lg:p-0"
                  >
                    <div className="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-[10px] border border-[#f5c842] text-[#f5c842]">
                      <Icon className="h-6 w-6" />
                    </div>

                    <div>
                      <p className="text-[13px] font-black uppercase text-white">
                        {feature.title}
                      </p>
                      <p className="mt-1 text-[13px] text-white/70">
                        {feature.text}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}